import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router";
import toast from "react-hot-toast";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import MultiSelectDropdown from "@/app/components/ui/SelectMenus/MultiSelectDropdown";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import Toggle from "@/app/components/ui/Toggles";
import Input from "@/app/components/ui/Inputs/Input";
import { useClinicListAPI } from "@/app/hooks/api_hooks/org/useClinicListAPI";
import { useCouponSubServiceListAPICoupon } from "@/app/hooks/api_hooks/coupon/useCouponSubServiceListAPICoupon";
import { useSaveBookingFeeConfigAPI } from "@/app/hooks/api_hooks/bookings/useSaveBookingFeeConfigAPI";
import { useRouter } from "@/app/hooks/useRouter";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import { dayOfWeekOptions, bookingFeeOptions, advanceDaysOptions } from "@/constants";

const CUSTOM_FEE_OPTION = { id: "other", title: "Other" };
const feeOptions = [...bookingFeeOptions, CUSTOM_FEE_OPTION];

type PasteField = "clinic_id" | "sub_service_id";

export default function BookingFeeConfigComponent() {
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const { execute: fetchClinics, clinicList } = useClinicListAPI();
  const { execute: fetchSubServices, subServiceList } = useCouponSubServiceListAPICoupon();
  const { execute: saveConfig, isLoading: saveLoading } = useSaveBookingFeeConfigAPI();
  const [formData, setFormData] = useState({
    clinic_id: [] as string[],
    sub_service_id: [] as string[],
    day_of_week: "",
    booking_fee_amount: "" as string | number,
    advance_days_for_booking_fee_applicability: [] as string[],
    vms_on_off_status: false,
    app_on_off_status: false,
    included_user_persona: "",
  });
  const [excludedPersonas, setExcludedPersonas] = useState<string[]>([]);
  const [newExcludedPersona, setNewExcludedPersona] = useState("");
  const [requiredPersonas, setRequiredPersonas] = useState<string[]>([]);
  const [newRequiredPersona, setNewRequiredPersona] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCustomFee, setIsCustomFee] = useState(false);
  const [pasteField, setPasteField] = useState<PasteField | null>(null);
  const [pastedIds, setPastedIds] = useState("");
  const [subServiceDropdownKey, setSubServiceDropdownKey] = useState(0);

  useEffect(() => {
    fetchClinics();
    fetchSubServices("", "");
  }, []);

  const clinicSelectItems = useMemo(() => {
    return Array.isArray(clinicList)
      ? clinicList.map((c: any) => ({
        id: c.id,
        title: (c.clinic_name ?? "").split("_").join(" "),
        checkboxVisible: true,
        withSubItems: false,
        subSelectItems: [],
        state: {}
      }))
      : [];
  }, [clinicList]);

  const subServiceSelectItems = useMemo(() => {
    return Array.isArray(subServiceList)
      ? subServiceList.map((s: any) => ({
        id: s.sub_service_id,
        title: (s.sub_service_name ?? "").split("_").join(" "),
        checkboxVisible: true,
        withSubItems: false,
        subSelectItems: [],
        state: {}
      }))
      : [];
  }, [subServiceList]);

  const selectedClinicItems = useMemo(() => {
    const ids = new Set(formData.clinic_id);
    return clinicSelectItems.filter((c) => ids.has(c.id));
  }, [formData.clinic_id, clinicSelectItems]);

  const selectedSubServiceNames = useMemo(() => {
    const ids = new Set(formData.sub_service_id);
    return subServiceSelectItems.filter((s) => ids.has(s.id)).map((s) => s.title);
  }, [formData.sub_service_id, subServiceSelectItems]);

  const clinicsText = useMemo(() => {
    if (formData.clinic_id.length === 0) return "no clinics selected";
    if (clinicSelectItems.length > 0 && formData.clinic_id.length === clinicSelectItems.length) return "all clinics";
    const selectedNames = selectedClinicItems.map((c) => c.title);
    if (selectedNames.length === 0) return "selected clinics";
    if (selectedNames.length === 1) return selectedNames[0];
    if (selectedNames.length === 2) return `${selectedNames[0]} and ${selectedNames[1]}`;
    return `${selectedNames.slice(0, -1).join(", ")}, and ${selectedNames[selectedNames.length - 1]}`;
  }, [formData.clinic_id, clinicSelectItems, selectedClinicItems]);

  const subServicesText = useMemo(() => {
    if (formData.sub_service_id.length === 0) return "no sub-services selected";
    if (subServiceSelectItems.length > 0 && formData.sub_service_id.length === subServiceSelectItems.length) return "all sub-services";
    const selectedNames = selectedSubServiceNames;
    if (selectedNames.length === 0) return "selected sub-services";
    if (selectedNames.length === 1) return selectedNames[0];
    if (selectedNames.length === 2) return `${selectedNames[0]} and ${selectedNames[1]}`;
    return `${selectedNames.slice(0, -1).join(", ")}, and ${selectedNames[selectedNames.length - 1]}`;
  }, [formData.sub_service_id, subServiceSelectItems, selectedSubServiceNames]);

  const daysText = useMemo(() => {
    if (!formData.day_of_week) return "any day of the week";
    if (formData.day_of_week === "All") return "any day of the week";
    if (formData.day_of_week === "WeekDay") return "weekdays (Monday to Friday)";
    if (formData.day_of_week === "WeekEnd") return "weekends (Saturday and Sunday)";
    return `${formData.day_of_week}s`;
  }, [formData.day_of_week]);

  const advanceDayLabel = (days: number) => {
    if (days === 0) return "D-Day";
    if (days === 1) return "1 day in advance";
    return `${days} days in advance`;
  };

  const applicabilityText = useMemo(() => {
    if (formData.advance_days_for_booking_fee_applicability.length === 0) return "";
    const labels = formData.advance_days_for_booking_fee_applicability
      .map((d) => advanceDayLabel(Number(d)));
    if (labels.length === 1) return `made on ${labels[0]}`;
    if (labels.length === 2) return `made on ${labels[0]} and ${labels[1]}`;
    return `made on ${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
  }, [formData.advance_days_for_booking_fee_applicability]);

  const personaText = useMemo(() => {
    if (!formData.included_user_persona) {
      return "all users (no user persona filtering applied)";
    }
    let text = `users matching the persona "${formData.included_user_persona}"`;
    if (requiredPersonas.length > 0) {
      text += ` who also hold ${requiredPersonas.map((p) => `"${p}"`).join(" and ")}`;
    }
    if (excludedPersonas.length > 0) {
      const excludedText = excludedPersonas.length === 1
        ? `"${excludedPersonas[0]}"`
        : excludedPersonas.length === 2
          ? `"${excludedPersonas[0]}" and "${excludedPersonas[1]}"`
          : `${excludedPersonas.slice(0, -1).map(p => `"${p}"`).join(", ")}, and "${excludedPersonas[excludedPersonas.length - 1]}"`;
      text += ` (excluding users with the persona ${excludedText})`;
    }
    return text;
  }, [formData.included_user_persona, excludedPersonas, requiredPersonas]);

  const DAY_GROUP_COUNTS: Record<string, number> = { All: 7, Weekday: 5, Weekend: 2 };

  const totalConfigCount = useMemo(() => {
    if (!formData.day_of_week) return 0;
    const dayCount = DAY_GROUP_COUNTS[formData.day_of_week] ?? 1;
    return (
      formData.clinic_id.length *
      formData.sub_service_id.length *
      dayCount *
      formData.advance_days_for_booking_fee_applicability.length
    );
  }, [
    formData.clinic_id,
    formData.sub_service_id,
    formData.day_of_week,
    formData.advance_days_for_booking_fee_applicability,
  ]);

  const personaRegex = /^[a-z_]+$/;

  const validateForm = (data = formData) => {
    const newErrors: Record<string, string> = {};

    if (!data.clinic_id || data.clinic_id.length === 0) {
      newErrors.clinic_id = "Clinic is required";
    }
    if (!data.sub_service_id || data.sub_service_id.length === 0) {
      newErrors.sub_service_id = "Sub-service is required";
    }
    if (!data.day_of_week) {
      newErrors.day_of_week = "Day of week is required";
    }
    if (data.booking_fee_amount === "") {
      newErrors.booking_fee_amount = "Booking fee amount is required";
    } else if (Number(data.booking_fee_amount) <= 0) {
      newErrors.booking_fee_amount = "Enter an amount greater than 0";
    }
    if (!data.advance_days_for_booking_fee_applicability || data.advance_days_for_booking_fee_applicability.length === 0) {
      newErrors.advance_days_for_booking_fee_applicability = "Advance days applicability is required";
    }
    if (!data.included_user_persona) {
      newErrors.included_user_persona = "Included persona is required";
    } else if (!personaRegex.test(data.included_user_persona)) {
      newErrors.included_user_persona = "Only lowercase letters and underscores are allowed";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (field: string, value: any) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    if (isSubmitted) {
      validateForm(updated);
    }
  };

  const handleFeeSelect = (item: ISelectMenuItemData | null) => {
    const isCustom = item?.id === CUSTOM_FEE_OPTION.id;
    setIsCustomFee(isCustom);
    handleFieldChange("booking_fee_amount", item && !isCustom ? Number(item.id) : "");
  };

  const handleSubServiceSelect = useCallback((ids: string[]) => {
    setFormData((prev) => ({ ...prev, sub_service_id: ids }));
    setErrors((prev) => ({ ...prev, sub_service_id: ids.length ? undefined : prev.sub_service_id }));
  }, []);

  const parsedPastedIds = [...new Set(pastedIds.split(/[\s,"']+/).filter(Boolean))];

  const openPasteBox = (field: PasteField) => {
    setPasteField(field);
    setPastedIds("");
  };

  const handleApplyPastedIds = () => {
    const items = pasteField === "clinic_id" ? clinicSelectItems : subServiceSelectItems;
    const validIds = new Set(items.map((i) => i.id));
    const matched = parsedPastedIds.filter((id) => validIds.has(id));
    const notFound = parsedPastedIds.length - matched.length;

    if (matched.length === 0) {
      toast.error("None of the pasted IDs match. Check the IDs and try again.");
      return;
    }

    handleFieldChange(pasteField, [...new Set([...formData[pasteField], ...matched])]);
    if (pasteField === "sub_service_id") setSubServiceDropdownKey((k) => k + 1);
    setPasteField(null);
    toast.success(notFound ? `${matched.length} selected, ${notFound} not found` : `${matched.length} selected`);
  };

  const renderPasteBox = () => (
    <div className="mt-2 rounded-lg border border-gray-200 bg-slate-50 p-2">
      <textarea
        rows={4}
        autoFocus
        value={pastedIds}
        onChange={(e) => setPastedIds(e.target.value)}
        placeholder="Paste IDs separated by commas or new lines"
        className="w-full resize-none rounded-md border border-gray-300 bg-white px-2.5 py-2 text-xs font-medium text-black placeholder:font-normal placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-[#193A6A] focus:border-[#193A6A]"
      />
      <div className="mt-1.5 flex items-center justify-between">
        <span className="text-[11px] text-slate-400">
          {parsedPastedIds.length} ID{parsedPastedIds.length === 1 ? "" : "s"}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setPasteField(null)}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={parsedPastedIds.length === 0}
            onClick={handleApplyPastedIds}
            className="px-3 py-1.5 rounded-md bg-[#193A6A] hover:bg-[#11274c] text-white text-xs font-medium disabled:bg-slate-200 disabled:text-slate-400"
          >
            Select
          </button>
        </div>
      </div>
    </div>
  );

  const renderPasteTrigger = (field: PasteField) =>
    pasteField !== field && (
      <button
        type="button"
        onClick={() => openPasteBox(field)}
        className="ml-auto text-[11px] font-medium text-[#193A6A] hover:underline"
      >
        Paste IDs
      </button>
    );

  const handleAddExcludedPersona = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const persona = newExcludedPersona.trim();
    if (!persona) return;

    if (!personaRegex.test(persona)) {
      setErrors((prev) => ({
        ...prev,
        excluded_user_personas: "Persona must only contain lowercase letters and underscores",
      }));
      return;
    }

    if (excludedPersonas.includes(persona)) {
      setErrors((prev) => ({
        ...prev,
        excluded_user_personas: "Persona has already been added to the excluded list",
      }));
      return;
    }

    if (requiredPersonas.includes(persona)) {
      setErrors((prev) => ({
        ...prev,
        excluded_user_personas: "Persona is in the required list; a persona cannot be both required and excluded",
      }));
      return;
    }

    const updatedPersonas = [...excludedPersonas, persona];
    setExcludedPersonas(updatedPersonas);
    setNewExcludedPersona("");
    setErrors((prev) => {
      const copy = { ...prev };
      delete copy.excluded_user_personas;
      return copy;
    });

    if (isSubmitted) {
      validateForm(formData);
    }
  };

  const handleRemoveExcludedPersona = (personaToRemove: string) => {
    const updatedPersonas = excludedPersonas.filter((p) => p !== personaToRemove);
    setExcludedPersonas(updatedPersonas);
    if (isSubmitted) {
      validateForm(formData);
    }
  };

  const handleAddRequiredPersona = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const persona = newRequiredPersona.trim();
    if (!persona) return;

    if (!personaRegex.test(persona)) {
      setErrors((prev) => ({
        ...prev,
        required_user_personas: "Persona must only contain lowercase letters and underscores",
      }));
      return;
    }

    if (requiredPersonas.includes(persona)) {
      setErrors((prev) => ({
        ...prev,
        required_user_personas: "Persona has already been added to the required list",
      }));
      return;
    }

    if (excludedPersonas.includes(persona)) {
      setErrors((prev) => ({
        ...prev,
        required_user_personas: "Persona is in the excluded list; a persona cannot be both required and excluded",
      }));
      return;
    }

    setRequiredPersonas([...requiredPersonas, persona]);
    setNewRequiredPersona("");
    setErrors((prev) => {
      const copy = { ...prev };
      delete copy.required_user_personas;
      return copy;
    });
  };

  const handleRemoveRequiredPersona = (personaToRemove: string) => {
    setRequiredPersonas(requiredPersonas.filter((p) => p !== personaToRemove));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);

    const isValid = validateForm();
    if (!isValid) {
      toast.error("Please correct the validation errors in the form.");
      return;
    }

    const payload = {
      clinic_id: formData.clinic_id,
      sub_service_id: formData.sub_service_id,
      day_of_week: formData.day_of_week,
      booking_fee_amount: Number(formData.booking_fee_amount),
      advance_days_for_booking_fee_applicability: formData.advance_days_for_booking_fee_applicability.map(Number),
      vms_on_off_status: formData.vms_on_off_status,
      app_on_off_status: formData.app_on_off_status,
      included_user_persona: formData.included_user_persona,
      excluded_user_personas: excludedPersonas,
      // Sent only when set, so a save without requirements is unchanged.
      ...(requiredPersonas.length > 0 && { required_user_personas: requiredPersonas }),
    };

    const result = await saveConfig(payload);
    if (result.status) {
      setFormData({
        clinic_id: [],
        sub_service_id: [],
        day_of_week: "",
        booking_fee_amount: "",
        advance_days_for_booking_fee_applicability: [],
        vms_on_off_status: false,
        app_on_off_status: false,
        included_user_persona: "",
      });
      setExcludedPersonas([]);
      setRequiredPersonas([]);
      setIsCustomFee(false);
      setIsSubmitted(false);
      navigate(getRouteKey("BOOKING_FEE_CONFIG", "url"));
    }
  };

  const handleCancel = () => {
    navigate(getRouteKey("BOOKING_FEE_CONFIG", "url"));
  };

  const isFormValidState = useMemo(() => {
    return (
      formData.clinic_id.length > 0 &&
      formData.sub_service_id.length > 0 &&
      formData.day_of_week &&
      Number(formData.booking_fee_amount) > 0 &&
      formData.advance_days_for_booking_fee_applicability.length > 0 &&
      formData.included_user_persona &&
      personaRegex.test(formData.included_user_persona)
    );
  }, [formData]);



  return (
    <div className="bg-white rounded-md py-1">
      <div className="text-black text-base font-semibold mt-2 mx-3">
        New Booking Fee Configuration
      </div>
      <div className="mx-3 pb-24">
        <form onSubmit={handleSubmit}>

          {/* Clinic Name */}
          <div className="justify-start items-baseline flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[300px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Clinic Name </span>
              <span className="text-rose-600 text-xs font-normal">*</span>
              {formData.clinic_id.length > 0 && (
                <span className="text-slate-400 text-xs font-normal">({formData.clinic_id.length} selected)</span>
              )}
              {renderPasteTrigger("clinic_id")}
            </div>
            <div className="w-[300px]">
              <SearchableMultiSelectMenu
                placeholder="Search and select clinics"
                selectItems={clinicSelectItems}
                register={{ name: "clinic_id" } as any}
                onSelectItem={(items) => {
                  const ids = items ? items.map((i) => i.id) : [];
                  handleFieldChange("clinic_id", ids);
                }}
                defaultSelected={selectedClinicItems}
                fieldError={errors.clinic_id ? { type: "required", message: errors.clinic_id } : undefined}
                errorMessages={errors.clinic_id ? [{ message: errors.clinic_id, type: "required" }] : []}
                showTypedErrors={true}
                withSelectAll={true}
                withDropDownSearch={true}
                expandedInputValue={true}
              />
              {pasteField === "clinic_id" && renderPasteBox()}
            </div>
          </div>

          {/* Sub-Service Name */}
          <div className="justify-start items-baseline flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[300px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Sub-Service Name </span>
              <span className="text-rose-600 text-xs font-normal">*</span>
              {formData.sub_service_id.length > 0 && (
                <span className="text-slate-400 text-xs font-normal">({formData.sub_service_id.length} selected)</span>
              )}
              {renderPasteTrigger("sub_service_id")}
            </div>
            <div className="w-[300px]">
              <div
                className={`[&_button]:px-3 [&_button]:shadow-none [&_input[type=text]]:text-xs ${
                  formData.sub_service_id.length === 0 ? "[&_button]:font-normal [&_button]:text-gray-400" : ""
                }`}
              >
                <MultiSelectDropdown
                  key={subServiceDropdownKey}
                  data={subServiceSelectItems}
                  selectedKey="id"
                  onSelectItem={handleSubServiceSelect}
                  defaultSelected={formData.sub_service_id}
                  register={() => ({})}
                  name="sub_service_id"
                  error={!!errors.sub_service_id}
                  containerClassName="w-full"
                />
              </div>
              {errors.sub_service_id && <p className="mt-1 text-xs text-red-600">{errors.sub_service_id}</p>}
              {pasteField === "sub_service_id" && renderPasteBox()}
            </div>
          </div>

          {/* Day of Week */}
          <div className="justify-start items-baseline flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[300px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Day of Week </span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <div className="w-[300px]">
              <SearchableSelectMenu
                placeholder="Select Day of Week"
                selectItems={dayOfWeekOptions}
                register={{ name: "day_of_week" } as any}
                onSelectItem={(item) => handleFieldChange("day_of_week", item ? item.id : "")}
                defaultSelected={dayOfWeekOptions.find((d) => d.id === formData.day_of_week)}
                fieldError={errors.day_of_week ? { type: "required", message: errors.day_of_week } : undefined}
                errorMessages={errors.day_of_week ? [{ message: errors.day_of_week, type: "required" }] : []}
                showDropdownIcon={true}
                showTypedErrors={true}
                disableSearch={true}
              />
            </div>
          </div>

          {/* Booking Fee Amount */}
          <div className="justify-start items-baseline flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[300px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Booking Fee Amount (₹) </span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <div className="w-[300px]">
              <SearchableSelectMenu
                placeholder="Select Fee Amount"
                selectItems={feeOptions}
                register={{ name: "booking_fee_amount" } as any}
                onSelectItem={handleFeeSelect}
                defaultSelected={
                  isCustomFee
                    ? CUSTOM_FEE_OPTION
                    : bookingFeeOptions.find((b) => b.id === String(formData.booking_fee_amount))
                }
                fieldError={errors.booking_fee_amount && !isCustomFee ? { type: "required", message: errors.booking_fee_amount } : undefined}
                errorMessages={errors.booking_fee_amount && !isCustomFee ? [{ message: errors.booking_fee_amount, type: "required" }] : []}
                showDropdownIcon={true}
                showTypedErrors={true}
                disableSearch={true}
              />
              {isCustomFee && (
                <Input
                  className="w-full mt-2"
                  inputMode="numeric"
                  maxLength={5}
                  autoFocus
                  placeholder="Enter amount"
                  value={String(formData.booking_fee_amount)}
                  onChange={(e) => handleFieldChange("booking_fee_amount", e.currentTarget.value.replace(/\D/g, ""))}
                  fieldError={errors.booking_fee_amount ? { type: "required", message: errors.booking_fee_amount } : undefined}
                  errorMessages={errors.booking_fee_amount ? [{ message: errors.booking_fee_amount, type: "required" }] : []}
                  register={{ name: "custom_booking_fee_amount" } as any}
                />
              )}
            </div>
          </div>

          {/* Advance Days for Applicability */}
          <div className="justify-start items-baseline flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[300px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Advance Days for Applicability </span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <div className="w-[300px]">
              <SearchableMultiSelectMenu
                placeholder="Select Advance Days"
                selectItems={advanceDaysOptions}
                register={{ name: "advance_days_for_booking_fee_applicability" } as any}
                onSelectItem={(items) => {
                  const ids = items ? items.map((i) => i.id) : [];
                  handleFieldChange("advance_days_for_booking_fee_applicability", ids);
                }}
                defaultSelected={advanceDaysOptions.filter((a) =>
                  formData.advance_days_for_booking_fee_applicability.includes(a.id),
                )}
                fieldError={errors.advance_days_for_booking_fee_applicability ? { type: "required", message: errors.advance_days_for_booking_fee_applicability } : undefined}
                errorMessages={errors.advance_days_for_booking_fee_applicability ? [{ message: errors.advance_days_for_booking_fee_applicability, type: "required" }] : []}
                showTypedErrors={true}
                withSelectAll={true}
                withDropDownSearch={false}
                expandedInputValue={true}
              />
            </div>
          </div>

          {/* VMS Toggle */}
          <div className="justify-start items-center flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[300px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">VMS</span>
            </div>
            <div className="w-[300px]">
              <Toggle
                checked={formData.vms_on_off_status}
                onToggle={(checked) => handleFieldChange("vms_on_off_status", checked)}
              />
            </div>
          </div>

          {/* App Toggle */}
          <div className="justify-start items-center flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[300px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">App</span>
            </div>
            <div className="w-[300px]">
              <Toggle
                checked={formData.app_on_off_status}
                onToggle={(checked) => handleFieldChange("app_on_off_status", checked)}
              />
            </div>
          </div>

          {/* Included User Persona */}
          <div className="justify-start items-baseline flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[300px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Included User Persona </span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <div className="w-[300px]">
              <Input
                className="w-full text-xs"
                placeholder="e.g. premium_guest_user"
                value={formData.included_user_persona}
                onChange={(e) => handleFieldChange("included_user_persona", e.currentTarget.value.toLowerCase().replace(/[^a-z_]/g, ""))}
                fieldError={errors.included_user_persona ? { type: "required", message: errors.included_user_persona } : undefined}
                errorMessages={errors.included_user_persona ? [{ message: errors.included_user_persona, type: "required" }] : []}
                register={{ name: "included_user_persona" } as any}
              />
            </div>
          </div>

          {/* Required User Personas */}
          <div className="justify-start items-baseline flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[300px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Required User Personas</span>
              <span className="text-slate-400 text-[11px]">(must hold all)</span>
            </div>
            <div className="w-[300px] space-y-3">
              <div className="flex gap-2">
                <Input
                  className="w-full text-xs"
                  placeholder="e.g. fb_lead"
                  value={newRequiredPersona}
                  onChange={(e) => setNewRequiredPersona(e.currentTarget.value.toLowerCase().replace(/[^a-z_]/g, ""))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddRequiredPersona();
                    }
                  }}
                  fieldError={errors.required_user_personas ? { type: "required", message: errors.required_user_personas } : undefined}
                  errorMessages={errors.required_user_personas ? [{ message: errors.required_user_personas, type: "required" }] : []}
                  register={{ name: "required_user_personas" } as any}
                />
                <button
                  type="button"
                  onClick={handleAddRequiredPersona}
                  className="px-4 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded-md text-xs font-medium transition-colors h-[34px]"
                >
                  Add
                </button>
              </div>

              {/* Tag List */}
              {requiredPersonas.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {requiredPersonas.map((persona) => (
                    <span
                      key={persona}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-800 border border-slate-200 transition-all duration-150 hover:bg-slate-200"
                    >
                      {persona}
                      <button
                        type="button"
                        onClick={() => handleRemoveRequiredPersona(persona)}
                        className="text-slate-400 hover:text-rose-600 transition-colors focus:outline-none font-bold text-sm"
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Excluded User Personas */}
          <div className="justify-start items-baseline flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[300px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Excluded User Personas</span>
            </div>
            <div className="w-[300px] space-y-3">
              <div className="flex gap-2">
                <Input
                  className="w-full text-xs"
                  placeholder="e.g. guest_b2b_user"
                  value={newExcludedPersona}
                  onChange={(e) => setNewExcludedPersona(e.currentTarget.value.toLowerCase().replace(/[^a-z_]/g, ""))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddExcludedPersona();
                    }
                  }}
                  fieldError={errors.excluded_user_personas ? { type: "required", message: errors.excluded_user_personas } : undefined}
                  errorMessages={errors.excluded_user_personas ? [{ message: errors.excluded_user_personas, type: "required" }] : []}
                  register={{ name: "excluded_user_personas" } as any}
                />
                <button
                  type="button"
                  onClick={handleAddExcludedPersona}
                  className="px-4 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded-md text-xs font-medium transition-colors h-[34px]"
                >
                  Add
                </button>
              </div>

              {/* Tag List */}
              {excludedPersonas.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {excludedPersonas.map((persona) => (
                    <span
                      key={persona}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-800 border border-slate-200 transition-all duration-150 hover:bg-slate-200"
                    >
                      {persona}
                      <button
                        type="button"
                        onClick={() => handleRemoveExcludedPersona(persona)}
                        className="text-slate-400 hover:text-rose-600 transition-colors focus:outline-none font-bold text-sm"
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Selection Details Preview */}
          {formData.clinic_id.length > 0 && formData.sub_service_id.length > 0 && (
            <div className="mt-8 p-5 bg-blue-50 bg-opacity-50 border border-blue-100 rounded-lg text-xs text-slate-700 space-y-3 mb-6">
              <div className="flex items-center gap-1.5 font-semibold text-[#193A6A] text-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-[#193A6A]"></span>
                Rule Summary
              </div>
              <div className="leading-relaxed space-y-2 text-slate-600">
                <p>
                  A booking fee of{" "}
                  <strong className="text-slate-900 bg-blue-100 bg-opacity-60 px-1.5 py-0.5 rounded">
                    {formData.booking_fee_amount !== "" ? `₹${formData.booking_fee_amount}` : "___"}
                  </strong>{" "}
                  will be charged for appointments booked at{" "}
                  <strong className="text-slate-900">{clinicsText}</strong> for{" "}
                  <strong className="text-slate-900">{subServicesText}</strong>.
                </p>
                
                <p>
                  This fee applies to appointments on{" "}
                  <strong className="text-slate-900">{daysText}</strong>, specifically for bookings{" "}
                  <strong className="text-slate-900">
                    {applicabilityText || "___"}
                  </strong>.
                </p>

                <p>
                  This rule applies only to{" "}
                  <strong className="text-slate-900">{personaText}</strong>.
                </p>
              </div>
              {totalConfigCount > 0 && (
                <div className="pt-2 border-t border-blue-100 font-medium text-[#193A6A]">
                  This rule covers {totalConfigCount} clinic, sub-service, day and advance-day combination{totalConfigCount === 1 ? "" : "s"}.
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="bottom-0 fixed left-0 right-0 px-10 py-3 justify-end items-center gap-4 flex border-t-2 bg-white z-30">
            <div
              onClick={handleCancel}
              className="flex justify-center items-center gap-2 bg-slate-400 bg-opacity-10 px-10 py-[9px] border rounded-md h-9 cursor-pointer"
            >
              <div className="font-medium text-black text-xs">Cancel</div>
            </div>
            <input
              type="submit"
              disabled={saveLoading || !isFormValidState}
              className={`flex justify-center items-center gap-2 border-slate-400 px-10 py-[9px] border border-opacity-20 rounded-md h-9 font-medium text-white text-xs cursor-pointer ${saveLoading || !isFormValidState
                ? "bg-slate-200 text-slate-400 cursor-not-allowed border-none"
                : "bg-[#193A6A] hover:bg-[#11274c]"
                }`}
              value={saveLoading ? "Saving..." : "Save"}
            />
          </div>

        </form>
      </div>
    </div>
  );
}
